import type { TrainedLabel } from '../data/types';
import './ConfidenceBars.css';

/**
 * Per-class confidence bars with honest values.
 *
 * AGENTS.md: "Confidence bars never hide that softmax sums to 1. When the
 * highest confidence is 38%, that's shown plainly, not rounded up or styled
 * as success." — bar widths are the raw probability; percentages render
 * with `Math.round` only for readability; the top class is highlighted only
 * when its confidence clears the uncertainty threshold.
 */

/** Threshold for the OOD-honesty hint. Below this, the UI says "I'm not sure". */
export const UNCERTAINTY_THRESHOLD = 0.5;

export interface ConfidenceBarsProps {
  labels: TrainedLabel[];
  probabilities: number[];
}

export function ConfidenceBars({ labels, probabilities }: ConfidenceBarsProps): JSX.Element {
  const top = topPrediction(labels, probabilities);
  const isUncertain = !top || top.confidence < UNCERTAINTY_THRESHOLD;

  return (
    <div className="bl-bars" aria-label="Predictions">
      {isUncertain ? (
        <p
          className="bl-bars__verdict bl-bars__verdict--uncertain"
          role="status"
          aria-live="polite"
        >
          I’m not sure what this is.
        </p>
      ) : (
        <p className="bl-bars__verdict" role="status" aria-live="polite">
          Looks like <strong>{top.className}</strong>.
        </p>
      )}
      <div
        className="bl-bars__list-wrap"
        data-long={labels.length > 8 ? 'true' : undefined}
        aria-label={labels.length > 8 ? `All ${labels.length} class confidence scores` : undefined}
      >
        <ol className="bl-bars__list">
          {labels.map((label, index) => {
            const confidence = probabilities[index] ?? 0;
            const isTop = !isUncertain && top && top.classId === label.classId;
            const percent = Math.round(confidence * 100);
            return (
              <li
                key={label.classId}
                className={`bl-bars__row${isTop ? ' bl-bars__row--top' : ''}`}
              >
                <span className="bl-bars__name">{label.name}</span>
                <div
                  className="bl-bars__bar"
                  role="progressbar"
                  aria-label={`Confidence for ${label.name}`}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={percent}
                >
                  <div
                    className="bl-bars__bar-fill"
                    style={{ width: `${Math.max(0, Math.min(100, confidence * 100))}%` }}
                  />
                </div>
                <span className="bl-bars__pct" aria-hidden="true">
                  {percent}%
                </span>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}

interface TopPrediction {
  classId: string;
  className: string;
  confidence: number;
}

function topPrediction(labels: TrainedLabel[], probabilities: number[]): TopPrediction | null {
  if (labels.length === 0) return null;
  let bestIndex = 0;
  let bestConfidence = probabilities[0] ?? 0;
  for (let i = 1; i < labels.length; i++) {
    const value = probabilities[i] ?? 0;
    if (value > bestConfidence) {
      bestConfidence = value;
      bestIndex = i;
    }
  }
  const label = labels[bestIndex];
  if (!label) return null;
  return { classId: label.classId, className: label.name, confidence: bestConfidence };
}
